import { parseEntityRef } from "@backstage/catalog-model";
import { Content, InfoCard, MarkdownContent } from "@backstage/core-components";
import { useRouteRef } from "@backstage/core-plugin-api";
import { EntityRefLink } from "@backstage/plugin-catalog-react";
import scaffolderPlugin from "@backstage/plugin-scaffolder/alpha";
import { type TemplateEntityV1beta3 } from "@backstage/plugin-scaffolder-common";
import { Box, ButtonLink, Flex, Text } from "@backstage/ui";

const TEMPLATE_REPLACED_BY_ANNOTATION = "backstage.io/template-replaced-by";

type Props = { entity: TemplateEntityV1beta3 };

/**
 * Renders a deprecation notice card for a scaffolder template marked as deprecated.
 *
 * Shows the template title and optional description. If the template's
 * `backstage.io/template-replaced-by` annotation names a replacement, a button
 * linking to the replacement's scaffolder form is displayed. Otherwise, if the
 * template has a known owner, a reach-out message with an
 * {@link EntityRefLink} is shown.
 *
 * @param props - {@link Props}
 */
export function DeprecatedTemplateNotice({ entity }: Props) {
    const templateRoute = useRouteRef(scaffolderPlugin.routes.selectedTemplate);
    const {
        title = entity.metadata.name,
        description,
        annotations,
    } = entity.metadata;
    const replacedBy = annotations?.[TEMPLATE_REPLACED_BY_ANNOTATION];
    const replacementRef = replacedBy
        ? parseEntityRef(replacedBy, {
              defaultKind: "Template",
              defaultNamespace: "default",
          })
        : undefined;
    // EntityRefLink applies defaultKind only to its label, not its URL, so
    // shorthand owners such as "banno/nebula" must be qualified first.
    const ownerRef = entity.spec.owner
        ? parseEntityRef(entity.spec.owner, {
              defaultKind: "Group",
              defaultNamespace: "default",
          })
        : undefined;

    return (
        <Content>
            <InfoCard
                title={`⛔ DEPRECATED - ${title}`}
                subheader={
                    description ? (
                        <MarkdownContent
                            content={description}
                            linkTarget="_blank"
                        />
                    ) : undefined
                }
                noPadding
                titleTypographyProps={{ component: "h2" }}
            >
                <Box p="8">
                    <Flex direction="column" gap="4">
                        <Text variant="body-medium">
                            This template has been deprecated and is no longer
                            available.
                        </Text>
                        {replacementRef && (
                            <Flex direction="row" justify="end">
                                <ButtonLink
                                    href={templateRoute({
                                        namespace: replacementRef.namespace,
                                        templateName: replacementRef.name,
                                    })}
                                    variant="primary"
                                    size="small"
                                >
                                    {"View Replacement >"}
                                </ButtonLink>
                            </Flex>
                        )}
                        {!replacementRef && ownerRef && (
                            <Text variant="body-medium">
                                No alternative template exists for this
                                deprecation, reach out to{" "}
                                <EntityRefLink
                                    entityRef={ownerRef}
                                    defaultKind="Group"
                                />{" "}
                                for more information.
                            </Text>
                        )}
                    </Flex>
                </Box>
            </InfoCard>
        </Content>
    );
}
